// Labelled form fields with a place for their translated field errors (SPEC §8.3).

import type { FieldError, Problem } from "../api/types";
import { translateCode } from "../i18n/i18n";
import { createElement } from "./dom";

export interface Field<T extends HTMLElement> {
  wrapper: HTMLLabelElement;
  input: T;
  error: HTMLSpanElement;
}

export interface SelectOption {
  value: string;
  label: string;
}

/** A text-like input with a label. */
export function createInputField(label: string, type: string, value: string): Field<HTMLInputElement> {
  const input: HTMLInputElement = createElement("input");
  input.type = type;
  input.value = value;
  return wrapField(label, input);
}

/** A multi-line text field. */
export function createTextAreaField(label: string, value: string): Field<HTMLTextAreaElement> {
  const input: HTMLTextAreaElement = createElement("textarea");
  input.value = value;
  input.rows = 2;
  return wrapField(label, input);
}

/** A dropdown with a label. */
export function createSelectField(label: string, options: SelectOption[], value: string): Field<HTMLSelectElement> {
  const input: HTMLSelectElement = createSelect(options, value);
  return wrapField(label, input);
}

/** A bare dropdown. */
export function createSelect(options: SelectOption[], value: string): HTMLSelectElement {
  const select: HTMLSelectElement = createElement("select");
  for (const option of options) {
    const element: HTMLOptionElement = createElement("option", "", option.label);
    element.value = option.value;
    select.append(element);
  }

  select.value = value;
  return select;
}

/** A checkbox with its label after it. */
export function createCheckbox(label: string, checked: boolean): Field<HTMLInputElement> {
  const input: HTMLInputElement = createElement("input");
  input.type = "checkbox";
  input.checked = checked;
  const wrapper: HTMLLabelElement = createElement("label", "checkbox-field");
  const error: HTMLSpanElement = createElement("span", "field-error");
  wrapper.append(input, createElement("span", "", label), error);
  return { wrapper, input, error };
}

/**
 * Shows each field error under the field with that name; returns the errors no field claimed, so the caller can
 * show them elsewhere.
 */
export function showFieldErrors(problem: Problem | null, fields: Record<string, Field<HTMLElement>>): FieldError[] {
  for (const field of Object.values(fields)) {
    field.error.textContent = "";
    field.wrapper.classList.remove("has-error");
  }

  const unclaimed: FieldError[] = [];
  for (const fieldError of problem?.fieldErrors ?? []) {
    const field: Field<HTMLElement> | undefined = fields[fieldError.field];
    if (field === undefined) {
      unclaimed.push(fieldError);
      continue;
    }

    field.error.textContent = translateCode("fieldErrors", fieldError.code, fieldError.params);
    field.wrapper.classList.add("has-error");
  }

  return unclaimed;
}

/** A translated list of field errors that belong to no visible field. */
export function describeFieldErrors(fieldErrors: FieldError[]): string {
  return fieldErrors
    .map((fieldError: FieldError): string => translateCode("fieldErrors", fieldError.code, fieldError.params))
    .join("; ");
}

function wrapField<T extends HTMLElement>(label: string, input: T): Field<T> {
  const wrapper: HTMLLabelElement = createElement("label", "field");
  const error: HTMLSpanElement = createElement("span", "field-error");
  wrapper.append(createElement("span", "field-label", label), input, error);
  return { wrapper, input, error };
}
